import React, { useEffect, useRef } from "react";
import {
  Box,
  InputBase,
  Container,
  IconButton,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import {
  Search,
  Close,
  History,
  TrendingUp,
  NorthEast,
} from "@mui/icons-material";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { useSearch } from "./SearchContext";
import { MEMBERSHIP_COLORS } from "./MembershipStyles";

const MembershipSearchOverlay: React.FC = () => {
  const { isSearchOpen, openSearch, closeSearch } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);

  // Mouse tracking for spotlight effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 150 };
  const spotlightX = useSpring(mouseX, springConfig);
  const spotlightY = useSpring(mouseY, springConfig);

  const spotlightBackground = useTransform(
    [spotlightX, spotlightY],
    ([x, y]) =>
      `radial-gradient(circle 400px at ${x}px ${y}px, ${MEMBERSHIP_COLORS.accent}15, transparent 100%)`,
  );

  const handleMouseMove = (event: React.MouseEvent) => {
    const { clientX, clientY } = event;
    mouseX.set(clientX);
    mouseY.set(clientY);
  };

  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        isSearchOpen ? closeSearch() : openSearch();
      }
      if (e.key === "Escape" && isSearchOpen) {
        closeSearch();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchOpen, openSearch, closeSearch]);

  const recentSearches = [
    "Facial Yoga",
    "Jawline Definition",
    "Eye Exercises",
    "Glow Routine",
  ];
  const trending = ["Anti-Aging", "Morning Routine", "Neck Tightening"];

  return (
    <AnimatePresence>
      {isSearchOpen && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
          animate={{ opacity: 1, backdropFilter: "blur(20px)" }}
          exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
          onMouseMove={handleMouseMove}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Spotlight Effect Background */}
          <motion.div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: spotlightBackground,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
          {/* Header/Input Area */}
          <Box
            component={motion.div}
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 200 }}
            sx={{
              py: { xs: 4, md: 8 },
              borderBottom: "1px solid rgba(0,0,0,0.05)",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Container maxWidth="md">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <Search
                    sx={{
                      color: MEMBERSHIP_COLORS.accent,
                      fontSize: "2.5rem",
                      mr: 3,
                    }}
                  />
                </motion.div>
                <InputBase
                  autoFocus
                  inputRef={inputRef}
                  placeholder="Type to find your flow..."
                  fullWidth
                  sx={{
                    fontSize: { xs: "1.5rem", md: "3rem" },
                    fontWeight: 800,
                    color: MEMBERSHIP_COLORS.text,
                    letterSpacing: "-0.02em",
                    "& input::placeholder": {
                      color: "rgba(0,0,0,0.1)",
                      opacity: 1,
                    },
                  }}
                />
                <motion.div
                  whileHover={{ rotate: 90, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    onClick={closeSearch}
                    sx={{
                      ml: 2,
                      backgroundColor: "rgba(0,0,0,0.05)",
                      p: 1.5,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
                    }}
                  >
                    <Close />
                  </IconButton>
                </motion.div>
              </Box>
            </Container>
          </Box>

          {/* Suggestions Area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              py: 6,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Container maxWidth="md">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {/* Recent Searches */}
                <Box sx={{ flex: 1, minWidth: "280px" }}>
                  <Typography
                    component={motion.span}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    variant="overline"
                    sx={{
                      color: MEMBERSHIP_COLORS.secondary,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      fontWeight: 800,
                      letterSpacing: "0.2em",
                      fontSize: "0.75rem",
                    }}
                  >
                    <History sx={{ fontSize: "1.2rem" }} /> RECENT SEARCHES
                  </Typography>
                  <Box
                    component={motion.div}
                    variants={{
                      hidden: { opacity: 0 },
                      show: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.1,
                        },
                      },
                    }}
                    initial="hidden"
                    animate="show"
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                  >
                    {recentSearches.map((term, i) => (
                      <Chip
                        key={term}
                        component={motion.div}
                        variants={{
                          hidden: { opacity: 0, scale: 0.8, y: 10 },
                          show: { opacity: 1, scale: 1, y: 0 },
                        }}
                        whileHover={{
                          scale: 1.05,
                          backgroundColor: MEMBERSHIP_COLORS.accent,
                          color: "#FFFFFF",
                          boxShadow: `0 10px 20px ${MEMBERSHIP_COLORS.accent}30`,
                        }}
                        whileTap={{ scale: 0.95 }}
                        label={term}
                        onClick={() => {}}
                        sx={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid rgba(0,0,0,0.06)",
                          fontWeight: 600,
                          px: 1,
                          py: 2.5,
                          borderRadius: "14px",
                          transition:
                            "background-color 0.2s, color 0.2s, border-color 0.2s",
                          "&:hover": {
                            borderColor: MEMBERSHIP_COLORS.accent,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Trending Areas */}
                <Box sx={{ flex: 1, minWidth: "280px" }}>
                  <Typography
                    component={motion.span}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    variant="overline"
                    sx={{
                      color: MEMBERSHIP_COLORS.secondary,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      fontWeight: 800,
                      letterSpacing: "0.2em",
                      fontSize: "0.75rem",
                    }}
                  >
                    <TrendingUp sx={{ fontSize: "1.2rem" }} /> TRENDING NOW
                  </Typography>
                  <Box
                    component={motion.div}
                    variants={{
                      hidden: { opacity: 0 },
                      show: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.1,
                          delayChildren: 0.2,
                        },
                      },
                    }}
                    initial="hidden"
                    animate="show"
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {trending.map((term) => (
                      <Box
                        key={term}
                        component={motion.div}
                        variants={{
                          hidden: { opacity: 0, x: -30 },
                          show: { opacity: 1, x: 0 },
                        }}
                        whileHover={{ x: 10 }}
                        sx={{
                          p: 2.5,
                          borderRadius: "16px",
                          cursor: "pointer",
                          transition: "all 0.3s cubic-bezier(0.19, 1, 0.22, 1)",
                          backgroundColor: "rgba(255,255,255,0.4)",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                          },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          group: "trending-item",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: MEMBERSHIP_COLORS.accent,
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "1.05rem" }}
                          >
                            {term}
                          </Typography>
                        </Stack>
                        <NorthEast
                          sx={{ fontSize: "1.2rem", color: "rgba(0,0,0,0.15)" }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Advanced Tip */}
              <Box
                component={motion.div}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, type: "spring", damping: 20 }}
                sx={{
                  mt: 10,
                  p: 4,
                  borderRadius: "24px",
                  background: `linear-gradient(135deg, ${MEMBERSHIP_COLORS.accent}15 0%, ${MEMBERSHIP_COLORS.accent}05 100%)`,
                  border: `1px solid ${MEMBERSHIP_COLORS.accent}20`,
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: MEMBERSHIP_COLORS.secondary,
                    fontStyle: "italic",
                    fontWeight: 500,
                  }}
                >
                  "The secret to a youthful face isn't a product, it's
                  persistence."
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 2,
                    display: "block",
                    color: MEMBERSHIP_COLORS.accent,
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                  }}
                >
                  LEVEL UP YOUR PRACTICE • SEARCH BY MUSCLE GROUP
                </Typography>
              </Box>
            </Container>
          </Box>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default MembershipSearchOverlay;
